import { toWSDate, WSGrid, WSText } from "@wingspanhq/fe-component-library";
import {
  IRequirementDefinitionResponse,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces";
import { useMemo } from "react";
import {
  FieldView,
  FieldViewProps
} from "../../../../shared/components/FieldView/FieldView";
import { ISignedDocumentTemplateResponse } from "@wingspanhq/signed-documents/dist/lib/interfaces";
import { ISharedFileRequestSettingsResponse } from "@wingspanhq/shared-files/dist/lib/interfaces";
import { mimeTypesToLabel } from "../../modules/SharedFile/createRequirementDefinition/utils";
import { IBackgroundCheckSettingsResponse } from "@wingspanhq/background-checks/dist/lib/interfaces";
import { RequirementInstructionsView } from "../../../Requirements/components/RequirementInstructionsView/RequirementInstructionsView";
import { IDENTITY_VERIFICATION_REQUIREMENT_INSTRUCTIONS } from "../../../Requirements/modules/IdentityVerification/instructions";
import { IInsuranceCoverageMonitorSettings } from "../../../../services/api/insurance/monitorSettings/types";
import { getRequirementDefinitionTemplateByParams } from "../../utils/getRequirementDefinitionTemplateByParams";
import { InsuranceInstructionsView } from "../../routes/RouteCreateRequirementByTemplate/components/InsuranceInstructionsView";
import { selectorIsAcceptedSourceTypeIncluded } from "../../../Requirements/modules/InsuranceCoverage/selectors/selectorIsAcceptedSourceTypeIncluded";
import { selectorPolicyTypesFromSettings } from "../../../Requirements/modules/InsuranceCoverage/selectors/selectorPolicyTypesFromSettings";

type Props = {
  requirementDefinition: IRequirementDefinitionResponse;
  singedDocumentTemplate?: ISignedDocumentTemplateResponse;
  sharedFileSettings?: ISharedFileRequestSettingsResponse;
  backgroundChecksSettings?: IBackgroundCheckSettingsResponse;
  monitorSettings?: IInsuranceCoverageMonitorSettings;
};

export const RequirementInformation: React.FC<Props> = ({
  requirementDefinition,
  singedDocumentTemplate,
  sharedFileSettings,
  backgroundChecksSettings,
  monitorSettings
}) => {
  const fields = useMemo(() => {
    const fieldsByType: Record<RequirementType, FieldViewProps[]> = {
      [RequirementType.Signature]: [
        {
          label: "Required signatures",
          value:
            singedDocumentTemplate?.roles?.length === 2
              ? "Both contractors and client signatures required"
              : "Contractor signature required"
        }
      ],
      [RequirementType.SharedFile]: [
        {
          label: "File types",
          value: mimeTypesToLabel(sharedFileSettings?.mimeTypes || [])
        },
        {
          label: "Instructions",
          value: sharedFileSettings?.instructions ? (
            <RequirementInstructionsView
              instructions={sharedFileSettings.instructions}
            />
          ) : undefined,
          fullWidth: true
        }
      ],
      [RequirementType.BackgroundCheck]: [
        {
          label: "Package",
          value: backgroundChecksSettings?.package?.title
        },
        {
          label: "Name",
          value: backgroundChecksSettings?.title
        },
        {
          label: "Instructions",
          value: backgroundChecksSettings?.instructions ? (
            <RequirementInstructionsView
              instructions={backgroundChecksSettings.instructions}
            />
          ) : undefined,
          fullWidth: true
        }
      ],
      [RequirementType.IdentityVerification]: [
        {
          label: "Instructions",
          value: (
            <RequirementInstructionsView
              instructions={IDENTITY_VERIFICATION_REQUIREMENT_INSTRUCTIONS}
            />
          ),
          fullWidth: true
        }
      ],
      [RequirementType.InsuranceCoverage]: [
        {
          label: "Instructions",
          value: (
            <InsuranceInstructionsView
              insuranceName={
                getRequirementDefinitionTemplateByParams({
                  type: RequirementType.InsuranceCoverage,
                  policyType: selectorPolicyTypesFromSettings(monitorSettings)
                }).name
              }
              allowMasterPolicy={selectorIsAcceptedSourceTypeIncluded(
                "PlanEnrollment",
                monitorSettings
              )}
            />
          ),
          fullWidth: true
        }
      ]
    };

    const result: FieldViewProps[] = [
      {
        label: "Creation date",
        value: toWSDate(requirementDefinition.createdAt).formattedValue
      },
      {
        label: "Expiration",
        value: "Does not expire"
      },
      {
        label: "Description",
        value: (
          <WSText.ParagraphSm as="pre">
            {requirementDefinition.description}
          </WSText.ParagraphSm>
        ),
        fullWidth: true
      },
      ...fieldsByType[requirementDefinition.requirementType]
    ].filter(i => i.value);

    return result;
  }, [
    singedDocumentTemplate,
    sharedFileSettings,
    requirementDefinition,
    backgroundChecksSettings
  ]);

  return (
    <WSGrid gutter="2XL">
      {fields.map(({ label, value, fullWidth }) => (
        <WSGrid.Item
          key={label}
          span={fullWidth ? { xs: "12" } : { xs: "12", s: "6", l: "6" }}
        >
          <FieldView label={label} value={value} />
        </WSGrid.Item>
      ))}
    </WSGrid>
  );
};
